import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { LessonAttachment } from '../../services/backendService/backendService';
import PodiumView from '../_organisms/PodiumView';
import Poll from '../_organisms/Poll';
import ResponseGridView from '../_organisms/ResponseGridView';
import WordcloudView from '../_organisms/WordcloudView';
import { Slide, SlideElement } from './slide.types';
import SlideElementAttachmentPassage from './SlideElementAttachmentPassage';
import SlideElementIframe from './SlideElementIframe';
import SlideElementImage from './SlideElementImage';
import SlideElementShape from './SlideElementShape';
import SlideElementSvg from './SlideElementSvg';
import SlideElementText from './SlideElementText';
import {
  isSlideElementAttachmentPassage,
  isSlideElementIframe,
  isSlideElementImage,
  isSlideElementShape,
  isSlideElementSvg,
  isSlideElementText,
} from './slideUtil';

const SlideResultViewer = ({
  elements,
  slide,
  showStaticPoll,
  tenantId,
  gameId,
  slides,
  isAuthenticated,
  onSlideWasPlayedTimeout,
  isDiscoverTemplate,
  isUsingSockets,
  isOwner,
  uniqueId,
  refetchData,
  lessonAttachments,
  toggleSlideOrResults,
}: {
  elements: SlideElement[];
  slide: Slide;
  showStaticPoll: boolean;
  tenantId?: string;
  gameId?: string;
  slides?: Slide[];
  onSlideWasPlayedTimeout?: () => void;
  isDiscoverTemplate: boolean;
  isUsingSockets?: boolean;
  isAuthenticated: boolean;
  isOwner: boolean;
  uniqueId?: string;
  refetchData?: boolean;
  lessonAttachments?: LessonAttachment[];
  toggleSlideOrResults?: () => void;
}) => {
  return (
    <>
      {elements.map((element, index) => (
        <SlideElementContainer
          key={index}
          $selected={false}
          $left={element.x}
          $top={element.y}
          $angle={element.angle}
          $opacity={element.opacity}
          $height={element.height}
          $width={element.width}
          onClick={() => toggleSlideOrResults?.()}
        >
          <div
            style={{
              width: element.width / (element.scale || 1),
              height: element.height / (element.scale || 1),
              transform: `scale(${(element.scale || 1) *
                (element.flip?.x ? -1 : 1)}, ${(element.scale || 1) *
                (element.flip?.y ? -1 : 1)})`,
              transformOrigin: element.flip ? 'center' : 'top left',
            }}
          >
            {isSlideElementImage(element) && (
              <SlideElementImage
                element={element}
                isAuthenticated={isAuthenticated && !!isUsingSockets}
                isDiscoverTemplate={isDiscoverTemplate}
                isOwner={isOwner}
              />
            )}
            {isSlideElementSvg(element) && (
              <SlideElementSvg
                element={element}
                isAuthenticated={isAuthenticated}
                isOwner={isOwner}
                isDiscoverTemplate={isDiscoverTemplate}
              />
            )}
            {isSlideElementShape(element) && <SlideElementShape element={element} />}

            {isSlideElementIframe(element) && (
              <SlideElementIframe element={element} slideMode={'view'} />
            )}
            {isSlideElementText(element) && (
              <SlideElementText
                element={element}
                richTextEditorPrefix={'slide-result-viewer'}
                content={element.textValue}
              />
            )}
            {isSlideElementAttachmentPassage(element) && (
              <SlideElementAttachmentPassage
                element={element}
                slideMode="view"
                lessonAttachments={lessonAttachments}
                isFromDiscover={isDiscoverTemplate}
              />
            )}
          </div>
        </SlideElementContainer>
      ))}
      {slide.type === 'poll' && slide.pollOptions && showStaticPoll && (
        <PollContainer>
          <Poll
            staticData={slide.pollOptions.map(e => ({ ...e, count: 0 }))}
            isAuthenticated={isAuthenticated && !!isUsingSockets}
            hideCount={true}
          />
        </PollContainer>
      )}
      {slide.type === 'poll' && slide.pollOptions && !showStaticPoll && (
        <PollDataContainer>
          <Poll
            isAuthenticated={isAuthenticated && !!isUsingSockets}
            hideCount={false}
            gameId={gameId}
            roundId={slide.id}
          />
        </PollDataContainer>
      )}
      {slide.type === 'responsegrid' && gameId && tenantId && slides && (
        <ResponseGridContainer>
          <ResponseGridView
            gameId={gameId}
            rounds={slides}
            onRoundWasPlayedTimeout={onSlideWasPlayedTimeout}
            tenantId={tenantId}
            roundId={slide.id}
            isAuthenticated={isAuthenticated && !!isUsingSockets}
            isUsingSockets={isUsingSockets}
          />
        </ResponseGridContainer>
      )}
      {slide.type === 'podium' && gameId && tenantId && slides && (
        <ResponseGridContainer>
          <PodiumView
            gameId={gameId}
            slideId={slide.id}
            isAuthenticated={isAuthenticated && !!isUsingSockets}
            allSlides={slides}
            tenantId={tenantId}
            onRoundWasPlayedTimeout={onSlideWasPlayedTimeout}
          />
        </ResponseGridContainer>
      )}
      {slide.type === 'wordcloud' &&
        slide.wasPlayed === true &&
        gameId &&
        tenantId &&
        slides && (
          <WordcloudContainer key={slide.id}>
            <WordcloudView
              gameId={gameId}
              roundId={slide.id}
              uniqueId={uniqueId}
              isAuthenticated={isAuthenticated && !!isUsingSockets}
              refetchData={refetchData}
            />
          </WordcloudContainer>
        )}
    </>
  );
};

const SlideElementContainer = styled.div<{
  $selected?: boolean;
  $height?: number;
  $width: number;
  $top?: number;
  $left?: number;
  $angle?: number;
  $opacity?: number;
}>`
  position: absolute;
  top: ${({ $top }) => `${$top}px` || '0'};
  left: ${({ $left }) => `${$left}px` || '0'};
  background-color: 'transparent';
  border-radius: 10px;
  width: ${({ $width }) => ($width ? `${$width}px` : 'auto')};
  height: ${({ $height }) => ($height ? `${$height}px` : 'auto')};
  transform: ${({ $angle }) => `rotate(${$angle || 0}deg)`};
  transform-origin: center;
  opacity: ${({ $opacity }) => ($opacity !== undefined ? $opacity : 1)};
  p,
  input {
    margin: 0;
    border: none;
    outline: none;
  }

  ${({ $selected }) =>
    $selected &&
    css`
      outline: 4px solid ${theme.colors.orange};
    `};
`;

const PollContainer = styled.div`
  position: absolute;
  width: 90%;
  height: 10%;
  background-color: transparent;
  bottom: 25%;
  left: 5%;
`;
const PollDataContainer = styled(PollContainer)`
  position: absolute;
  width: 90%;
  height: 40%;
  background-color: transparent;
  bottom: 25%;
  left: 5%;
`;

const ResponseGridContainer = styled.div`
  position: absolute;
  width: 100%;
  height: fit-content;
  background-color: transparent;
  top: 25%;
  height: 75%;
  left: 0;
  overflow-y: auto;
`;

const WordcloudContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 75%;
  background-color: transparent;
  top: 25%;
  left: 0;
`;

export default SlideResultViewer;
